import { create } from 'zustand';
import { Plot, PlotCreate, PlotUpdate } from '../types/plot';
import { Attachment } from '../types/attachment';
import api from '../config/axios';
import { PLOTS } from '../config/apiPaths';
import { notifyEntityCreated, notifyEntityUpdated } from '../utils/entityNotifications';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface PlotStore {
  plots: Plot[];
  currentPlot: Plot | null;
  isLoading: boolean;
  error: string | null;

  // Plot operations
  fetchPlots: () => Promise<void>;
  fetchPlotById: (id: number) => Promise<void>;
  createPlot: (plotData: PlotCreate) => Promise<Plot>;
  updatePlot: (id: number, updates: PlotUpdate) => Promise<Plot>;
  deletePlot: (id: number) => Promise<void>;
  
  // Attachment operations
  uploadAttachment: (plotId: number, file: File) => Promise<Attachment>;
  deleteAttachment: (plotId: number, attachmentId: number) => Promise<void>;
  
  // Utility methods
  getPlotById: (id: number) => Plot | undefined;
  clearError: () => void;
}

interface PlotStatus {
  vacant: boolean;
  occupied: boolean;
  archived: boolean;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const transformPlotCoordinates = (plot: any): any => {
  // Handle old latitude/longitude format
  if (plot.latitude !== undefined && plot.longitude !== undefined) {
    return {
      ...plot,
      coordinates: [{
        id: 1,
        lat: plot.latitude,
        lng: plot.longitude
      }]
    };
  }
  
  // Handle new key-value coordinate format from database
  if (plot.coordinates && typeof plot.coordinates === 'object' && !Array.isArray(plot.coordinates)) {
    const coordinateArray = Object.entries(plot.coordinates).map(([key, value]) => {
      const [lat, lng] = (value as string).split(',').map(s => parseFloat(s.trim()));
      return {
        id: parseInt(key),
        lat,
        lng
      };
    }).sort((a, b) => a.id - b.id); // Sort by id to maintain order
    
    return {
      ...plot,
      coordinates: coordinateArray
    };
  }
  
  return plot;
};

const getPlotStatus = (plot: any): PlotStatus => {
  // Check if the plot is archived first
  if (plot.status_archived) {
    return { vacant: false, occupied: false, archived: true };
  }
  
  // Check for active contracts
  const now = new Date();
  const hasActiveContract = plot.contracts?.some((contract: any) => {
    const startDate = new Date(contract.startDate);
    const endDate = contract.endDate ? new Date(contract.endDate) : null;
    const isActive = startDate <= now && (!endDate || endDate >= now);
    return isActive && contract.isActive !== false;
  });
  
  return {
    vacant: !hasActiveContract,
    occupied: !!hasActiveContract,
    archived: false
  };
};


const transformPlot = (plot: any): Plot => {
  const transformed = transformPlotCoordinates(plot);
  return {
    ...transformed,
    status: getPlotStatus(transformed),
    attachments: transformed.attachments || []
  };
};

// ============================================================================
// ZUSTAND STORE
// ============================================================================

export const usePlotStore = create<PlotStore>((set, get) => ({
  // Initial state
  plots: [],
  currentPlot: null,
  isLoading: false,
  error: null,

  // Fetch all plots
  fetchPlots: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get(PLOTS.ALL);
      const plots = response.data.map(transformPlot);
      set({ plots, error: null });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      set({ error: errorMessage });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  // Fetch plot by ID
  fetchPlotById: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get(PLOTS.BY_ID(id));
      const currentPlot = transformPlot(response.data);
      set({ currentPlot, error: null });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      set({ error: errorMessage });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  // Create new plot
  createPlot: async (plotData: PlotCreate) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post(PLOTS.ALL, plotData);
      const newPlot = transformPlot(response.data);
      
      set(state => ({
        plots: [...state.plots, newPlot],
        error: null
      }));
      
      // Notify about the new plot creation
      notifyEntityCreated('plot', {
        id: newPlot.id,
        name: newPlot.title,
      });
      
      return newPlot;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      set({ error: errorMessage });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  // Update existing plot
  updatePlot: async (id: number, updates: PlotUpdate) => {
    set({ isLoading: true, error: null });
    try {
      // Create a clean update object with only the fields we want to send
      const cleanUpdates: any = {};
      const allowedFields = [
        'title', 'description', 'size', 'address', 'status_id', 'color',
        'soil', 'health_score', 'coordinates', 'features', 'notes',
        'start_date', 'end_date', 'tags', 'metadata', 'organization_id'
      ];

      // Only include allowed fields and ensure we don't send undefined values
      Object.entries(updates).forEach(([key, value]) => {
        if (allowedFields.includes(key) && value !== undefined) {
          cleanUpdates[key] = value;
        }
      });

      console.log('Sending update to API:', JSON.stringify(cleanUpdates, null, 2));
      
      const response = await api.put(PLOTS.BY_ID(id), cleanUpdates);
      const updatedPlot = transformPlot(response.data);
      
      set(state => ({
        plots: state.plots.map(plot => 
          plot.id === id ? updatedPlot : plot
        ),
        currentPlot: state.currentPlot?.id === id ? updatedPlot : state.currentPlot,
        error: null
      }));
      
      // Notify about the plot update
      notifyEntityUpdated('plot', {
        id: updatedPlot.id,
        name: updatedPlot.title,
      });
      
      return updatedPlot;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      set({ error: errorMessage });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  // Delete plot
  deletePlot: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(PLOTS.BY_ID(id));
      
      set(state => ({
        plots: state.plots.filter(plot => plot.id !== id),
        currentPlot: state.currentPlot?.id === id ? null : state.currentPlot,
        error: null
      }));
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      set({ error: errorMessage });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  // Upload attachment
  uploadAttachment: async (plotId: number, file: File) => {
    set({ isLoading: true, error: null });
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(PLOTS.ATTACHMENTS(plotId), formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const newAttachment = response.data;
      
      // Update plot attachments in state
      set(state => ({
        plots: state.plots.map(plot => 
          plot.id === plotId 
            ? { ...plot, attachments: [...(plot.attachments || []), newAttachment] }
            : plot
        ),
        currentPlot: state.currentPlot?.id === plotId
          ? { ...state.currentPlot, attachments: [...(state.currentPlot.attachments || []), newAttachment] }
          : state.currentPlot,
        error: null
      }));
      
      return newAttachment;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      set({ error: errorMessage });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  // Delete attachment
  deleteAttachment: async (plotId: number, attachmentId: number) => {
    set({ isLoading: true, error: null });
    try {
      await api.delete(`${PLOTS.BY_ID(plotId)}/attachments/${attachmentId}`);
      
      // Update plot attachments in state
      set(state => ({
        plots: state.plots.map(plot => 
          plot.id === plotId 
            ? { ...plot, attachments: (plot.attachments || []).filter(a => a.id !== attachmentId) }
            : plot
        ),
        currentPlot: state.currentPlot?.id === plotId
          ? { 
              ...state.currentPlot, 
              attachments: (state.currentPlot.attachments || []).filter(a => a.id !== attachmentId) 
            }
          : state.currentPlot,
        error: null
      }));
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      set({ error: errorMessage });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  // Get plot by ID (utility method)
  getPlotById: (id: number) => {
    return get().plots.find(plot => plot.id === id);
  },

  // Clear error state
  clearError: () => {
    set({ error: null });
  }
}));